import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

import { BrlFormatCurrency, FormatPoints } from "../../../lib";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderLabel = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: var(--text-dark, #1D1D1F);
  line-height: 1.5;
`;

const ToggleBtn = styled.button`
  background: none;
  border: none;
  font-size: 14px;
  color: var(--text-dark, #1D1D1F);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
`;

const DetailsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 0;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const Label = styled.span`
  font-size: 14px;
  color: var(--text-dark, #1D1D1F);
  line-height: 1.5;
`;

const Value = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-dark, #1D1D1F);
  line-height: 1.5;
  text-align: right;
`;

const Hr = styled.div`
  height: 1px;
  background: #D9D9D9;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding-top: 8px;
`;

const TotalLabel = styled.span`
  font-size: 14px;
  color: var(--text-dark, #1D1D1F);
  line-height: 1.5;
`;

const TotalValue = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: var(--text-dark, #1D1D1F);
  line-height: 1.5;
`;

export function formatValue(displayMode, money, points) {
  if (displayMode === "points") return `${FormatPoints(points)} pts`;
  if (displayMode === "both") {
    return `${BrlFormatCurrency(money)} ou ${FormatPoints(points)} pts`;
  }
  return BrlFormatCurrency(money);
}

export default function FinancialSummary({
  subtotalMoney,
  subtotalPoints,
  shippingMoney,
  shippingPoints,
  coupon,
  totalMoney,
  totalPoints,
  displayMode,
  children,
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const hasCoupon = Boolean(coupon);

  useEffect(() => {
    if (hasCoupon) {
      setDetailsOpen(true);
    }
  }, [hasCoupon]);

  return (
    <Wrap>
      <Header>
        <HeaderLabel>Pedido</HeaderLabel>
        <ToggleBtn type="button" onClick={() => setDetailsOpen((v) => !v)}>
          Ver detalhes{" "}
          {detailsOpen ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 15L12 9L6 15" stroke="#171717" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M6 9L12 15L18 9" stroke="#171717" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </ToggleBtn>
      </Header>

      {detailsOpen && (
        <DetailsWrap>
          {shippingMoney != null && (
            <Row>
              <Label>Frete do pedido:</Label>
              <Value>{formatValue(displayMode, shippingMoney, shippingPoints)}</Value>
            </Row>
          )}
          {hasCoupon && (
            <Row>
              <Label>Parabéns você ganhou {formatValue(displayMode, coupon.discountMoney, coupon.discountPoints)}</Label>
              <Value style={{ color: "#96143C" }}>
                -{formatValue(displayMode, coupon.discountMoney, coupon.discountPoints)}
              </Value>
            </Row>
          )}
          <Row>
            <Label>Subtotal do pedido:</Label>
            <Value>{formatValue(displayMode, subtotalMoney, subtotalPoints)}</Value>
          </Row>
        </DetailsWrap>
      )}

      <Hr />

      <TotalRow>
        <div>
          <TotalLabel>Total (com frete)</TotalLabel>
          <br />
          <TotalValue>{formatValue(displayMode, totalMoney, totalPoints)}</TotalValue>
        </div>
        {children && <div style={{ flexShrink: 0 }}>{children}</div>}
      </TotalRow>
    </Wrap>
  );
}

FinancialSummary.propTypes = {
  subtotalMoney: PropTypes.number.isRequired,
  subtotalPoints: PropTypes.number.isRequired,
  shippingMoney: PropTypes.number,
  shippingPoints: PropTypes.number,
  coupon: PropTypes.shape({
    discountMoney: PropTypes.number,
    discountPoints: PropTypes.number,
    code: PropTypes.string,
  }),
  totalMoney: PropTypes.number.isRequired,
  totalPoints: PropTypes.number.isRequired,
  displayMode: PropTypes.oneOf(["money", "points", "both"]).isRequired,
  children: PropTypes.node,
};
